import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Banner, Field, PasswordField } from "../../components/mui";
import { MdWarningAmber } from "react-icons/md";

/**
 * 重設密碼 — 從信件連結進來，網址帶 ?token=...
 *
 * token 由後端簽章，內含 5 分鐘到期時間，而且綁著「目前密碼的指紋」，
 * 所以密碼一改同一條連結就自動失效（不需要「已使用」欄位）。
 * 過期或已使用的判斷都在後端，這裡只負責把錯誤訊息顯示出來。
 */

/** 與後端 PasswordResetService.Reset 同一套規則；這裡只是即時提示 */
function checkPassword(pwd) {
  if (!pwd) return "請輸入新密碼";
  if (pwd.length < 8) return "長度至少需 8 個字元";
  if (!pwd.trim()) return "不可僅包含空白";
  if (/^\d+$/.test(pwd)) return "不可全為數字";
  return "";
}

function ResetPassword(props) {
  const { POST_ResetPassword } = props;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  /* 三種狀態共用同一張卡；寬度與留白跟忘記密碼頁一致 */
  const cardSx = { width: "100%", maxWidth: 480, my: 6, mx: "auto" };
  const contentSx = { p: { xs: 3, sm: 4 } };

  /* 連結沒帶 token 就沒得做，直接說清楚而不是讓人填完才失敗 */
  if (!token) {
    return (
      <Card component="section" sx={cardSx}>
        <CardContent sx={contentSx}>
          <Typography variant="h2" component="h1">
            連結不完整
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            此網址缺少重設代碼。請直接點選信件中的按鈕，
            或返回登入頁重新申請。
          </Typography>
          <Button variant="contained" fullWidth component={Link} to="/Auth/ForgotPassword">
            重新申請
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (done) {
    return (
      <Card component="section" sx={cardSx}>
        <CardContent sx={contentSx}>
          <Typography variant="h2" component="h1">
            密碼已更新
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            請使用新密碼登入。
          </Typography>
          <Button variant="contained" fullWidth onClick={() => navigate("/Auth/Login")}>
            前往登入
          </Button>
        </CardContent>
      </Card>
    );
  }

  const submit = (e) => {
    e.preventDefault();
    const bad = checkPassword(pwd);
    if (bad) { setErr(`新密碼${bad}`); return; }
    if (pwd !== confirm) { setErr("兩次輸入的新密碼不一致"); return; }
    setErr("");
    POST_ResetPassword(
      { Token: token, NewPassword: pwd },
      () => setDone(true),
      setLoading,
      (msg) => setErr(msg)
    );
  };

  const hint = pwd ? checkPassword(pwd) : "";

  return (
    <Card component="form" onSubmit={submit} sx={cardSx}>
      <CardContent sx={contentSx}>
        <Typography variant="h2" component="h1">
          設定新密碼
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          連結於 5 分鐘內有效，設定完成後即可使用新密碼登入。
        </Typography>

        <Stack spacing={2} sx={{ mt: 3 }}>
          {/* 還沒打或打對了是規則說明；過不了就換成錯誤句子，讀螢幕都靠 aria-describedby 讀到 */}
          <Field
            label="新密碼"
            htmlFor="rp-new"
            help={hint ? null : "至少 8 個字元，不可全為數字，亦不可包含帳號。"}
            helpId="rp-new-help"
            error={hint || null}
            errorId="rp-new-err"
          >
            <PasswordField
              placeholder="至少 8 個字元"
              id="rp-new"
              fullWidth
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoFocus
              autoComplete="new-password"
              error={!!hint}
              slotProps={{ htmlInput: { "aria-describedby": hint ? "rp-new-err" : "rp-new-help" } }}
            />
          </Field>

          <Field label="確認新密碼" htmlFor="rp-confirm">
            <PasswordField
              placeholder="請再次輸入新密碼"
              id="rp-confirm"
              fullWidth
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </Field>

          {err && (
            <Banner kind="attention" icon={<MdWarningAmber />}>
              {err}
            </Banner>
          )}

          <Button variant="contained" fullWidth type="submit" disabled={loading} loading={loading} loadingPosition="start">
            {loading ? "更新中…" : "更新密碼"}
          </Button>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button variant="text" component={Link} to="/Auth/ForgotPassword">
              連結已過期？重新申請
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

ResetPassword.propTypes = { POST_ResetPassword: PropTypes.func };
ResetPassword.defaultProps = { POST_ResetPassword: () => {} };

const mapDispatchToProps = (dispatch) => ({
  POST_ResetPassword(payload, callback, loading, onError) {
    dispatch({ type: "POST_ResetPassword", payload, callback, loading, onError });
  },
});

export default connect(null, mapDispatchToProps)(ResetPassword);
